import React, { useState, useEffect } from 'react';
import { Building } from '../types/building';
import { EconomicOptimizerService, EconomicChoice } from '../services/economicOptimizerService';
import { formatCurrency } from '../utils/currencyUtils';

interface EconomicOptimizerProps {
    building: Building;
    onBack: () => void;
}

const EconomicOptimizer: React.FC<EconomicOptimizerProps> = ({ building, onBack }) => {
    const [economicData, setEconomicData] = useState<any>(null);
    const [selectedChoice, setSelectedChoice] = useState<EconomicChoice | null>(null);
    const [showCashFlow, setShowCashFlow] = useState(false);

    useEffect(() => {
        const summary = EconomicOptimizerService.getEconomicSummary(building);
        setEconomicData(summary);
        setSelectedChoice(summary.recommendedChoice);
    }, [building]);

    const getRiskColor = (risk: string) => {
        switch (risk) {
            case 'Low': return 'bg-green-100 text-green-800';
            case 'Medium': return 'bg-yellow-100 text-yellow-800';
            case 'High': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getComplexityIcon = (complexity: string) => {
        switch (complexity) {
            case 'Simple': return '🔧';
            case 'Moderate': return '⚙️';
            case 'Complex': return '🏗️';
            default: return '🔧';
        }
    };

    if (!economicData) {
        return <div className="text-center py-12">Analyzing economic options...</div>;
    }

    return (
        <div className="min-h-screen bg-neutral-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-primary-600 hover:text-primary-800 flex items-center"
                    >
                        ← Back to Building Analysis
                    </button>
                    <h1 className="text-3xl font-bold text-neutral-900 mb-2">Economic Optimizer</h1>
                    <p className="text-lg text-neutral-600">
                        Top 3 most economical retrofit strategies for {building.propertyName}
                    </p>
                </div>

                {/* Economic Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="text-sm font-medium text-neutral-600">Best ROI Option</div>
                        <div className="text-2xl font-bold text-green-600">
                            {economicData.summary.bestROI.toFixed(1)}%
                        </div>
                        <div className="text-xs text-neutral-500">10-year return</div>
                    </div>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="text-sm font-medium text-neutral-600">Fastest Payback</div>
                        <div className="text-2xl font-bold text-blue-600">
                            {economicData.summary.fastestPayback.toFixed(1)} yrs
                        </div>
                        <div className="text-xs text-neutral-500">Time to break even</div>
                    </div>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="text-sm font-medium text-neutral-600">Max Savings</div>
                        <div className="text-2xl font-bold text-purple-600">
                            {EconomicOptimizerService.formatCurrencyShort(economicData.summary.maxSavings)}
                        </div>
                        <div className="text-xs text-neutral-500">10-year total</div>
                    </div>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="text-sm font-medium text-neutral-600">Min Investment</div>
                        <div className="text-2xl font-bold text-orange-600">
                            {EconomicOptimizerService.formatCurrencyShort(economicData.summary.minInvestment)}
                        </div>
                        <div className="text-xs text-neutral-500">Upfront cost</div>
                    </div>
                </div>

                {/* Top 3 Economic Choices */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h2 className="text-xl font-bold text-neutral-900 mb-6">Top 3 Economic Strategies</h2>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {economicData.allChoices.map((choice: EconomicChoice, index: number) => (
                            <div 
                                key={choice.id}
                                className={`border rounded-lg p-6 cursor-pointer transition-all ${
                                    selectedChoice?.id === choice.id 
                                        ? 'border-primary-500 bg-primary-50' 
                                        : 'border-neutral-200 hover:border-neutral-300'
                                }`}
                                onClick={() => setSelectedChoice(choice)}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center">
                                        <span className="text-2xl mr-3">{getComplexityIcon(choice.complexity)}</span>
                                        <div>
                                            <h3 className="font-bold text-neutral-900">{choice.name}</h3>
                                            <span className={`text-xs px-2 py-1 rounded-full ${getRiskColor(choice.riskLevel)}`}>
                                                {choice.riskLevel} Risk
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-primary-600">#{index + 1}</div>
                                        <div className="text-xs text-neutral-500">Recommended</div>
                                    </div>
                                </div>

                                <p className="text-sm text-neutral-600 mb-4">{choice.description}</p>

                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-neutral-600">Investment:</span>
                                        <span className="font-medium">{EconomicOptimizerService.formatCurrencyShort(choice.totalCost)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-neutral-600">Annual Savings:</span>
                                        <span className="font-medium text-green-600">{EconomicOptimizerService.formatCurrencyShort(choice.annualSavings)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-neutral-600">Payback:</span>
                                        <span className="font-medium">{choice.paybackPeriod.toFixed(1)} years</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-neutral-600">10-Year ROI:</span>
                                        <span className="font-bold text-green-600">{choice.roi10Year.toFixed(1)}%</span>
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t border-neutral-200">
                                    <div className="text-xs text-neutral-500 mb-2">Includes {choice.retrofitOptions.length} improvements:</div>
                                    <div className="text-xs text-neutral-600">
                                        {choice.retrofitOptions.slice(0, 2).map(opt => opt.name).join(', ')}
                                        {choice.retrofitOptions.length > 2 && ` +${choice.retrofitOptions.length - 2} more`}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Detailed Analysis for Selected Choice */}
                {selectedChoice && (
                    <div className="space-y-8">
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-bold text-neutral-900 mb-6">
                                Detailed Analysis: {selectedChoice.name}
                            </h2>

                            {/* Business Case Section */}
                            <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-blue-900 mb-3">💼 Business Case</h3>
                                <p className="text-blue-800 leading-relaxed">{selectedChoice.businessCase}</p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Financial Breakdown */}
                                <div>
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-4">Financial Overview</h3>
                                    <div className="space-y-4">
                                        <div className="bg-neutral-50 rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-medium text-neutral-600">Total Investment</span>
                                                <span className="text-lg font-bold text-red-600">
                                                    {EconomicOptimizerService.formatCurrencyShort(selectedChoice.totalCost)}
                                                </span>
                                            </div>
                                            <div className="text-xs text-neutral-500">
                                                {formatCurrency(selectedChoice.totalCost / building.grossFloorArea)} per sq ft
                                            </div>
                                        </div>

                                        <div className="bg-green-50 rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-medium text-neutral-600">Annual Savings</span>
                                                <span className="text-lg font-bold text-green-600">
                                                    {EconomicOptimizerService.formatCurrencyShort(selectedChoice.annualSavings)}
                                                </span>
                                            </div>
                                            <div className="text-xs text-green-600 space-y-1">
                                                <div>LL97 Fine Savings: {EconomicOptimizerService.formatCurrencyShort(selectedChoice.ll97SavingsAnnual)}</div>
                                                <div>Energy Cost Savings: {EconomicOptimizerService.formatCurrencyShort(selectedChoice.energySavingsAnnual)}</div>
                                            </div>
                                        </div>

                                        <div className="bg-purple-50 rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-medium text-neutral-600">Monthly Net Cash Flow</span>
                                                <span className="text-lg font-bold text-purple-600">
                                                    {selectedChoice.monthlyNetCashFlow >= 0 ? '+' : ''}
                                                    {EconomicOptimizerService.formatCurrencyShort(selectedChoice.monthlyNetCashFlow)}
                                                </span>
                                            </div>
                                            <div className="text-xs text-purple-600">
                                                After loan payments
                                            </div>
                                        </div>

                                        <div className="bg-blue-50 rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-medium text-neutral-600">Net Present Value</span>
                                                <span className="text-lg font-bold text-blue-600">
                                                    {EconomicOptimizerService.formatCurrencyShort(selectedChoice.netPresentValue)}
                                                </span>
                                            </div>
                                            <div className="text-xs text-blue-600">
                                                5% discount rate applied
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Financing Options */}
                                <div>
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-4">Financing Options</h3>
                                    <div className="space-y-3">
                                        {selectedChoice.financingOptions.map((option, index) => (
                                            <div 
                                                key={index} 
                                                className={`border rounded-lg p-4 ${
                                                    option.name === selectedChoice.bestFinancingOption.name 
                                                        ? 'border-green-500 bg-green-50' 
                                                        : 'border-neutral-200'
                                                }`}
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex items-center">
                                                        <h4 className="font-medium text-neutral-900">{option.name}</h4>
                                                        {option.name === selectedChoice.bestFinancingOption.name && (
                                                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                                Recommended
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-sm font-medium text-neutral-600">
                                                        {option.interestRate > 0 ? `${(option.interestRate * 100).toFixed(1)}% APR` : 'Cash'}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-neutral-600">Down Payment:</span>
                                                        <div className="font-medium">
                                                            {EconomicOptimizerService.formatCurrencyShort(option.downPayment)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-neutral-600">Monthly Payment:</span>
                                                        <div className="font-medium">
                                                            {option.monthlyPayment > 0 
                                                                ? EconomicOptimizerService.formatCurrencyShort(option.monthlyPayment)
                                                                : 'None'
                                                            }
                                                        </div>
                                                    </div>
                                                    {option.termYears > 0 && (
                                                        <>
                                                            <div>
                                                                <span className="text-neutral-600">Term:</span>
                                                                <div className="font-medium">{option.termYears} years</div>
                                                            </div>
                                                            <div>
                                                                <span className="text-neutral-600">Total Cost:</span>
                                                                <div className="font-medium">
                                                                    {EconomicOptimizerService.formatCurrencyShort(option.totalFinancingCost)}
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Key Benefits */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-semibold text-neutral-800 mb-4">🎯 Key Benefits</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {selectedChoice.keyBenefits.map((benefit, index) => (
                                    <div key={index} className="flex items-start space-x-3">
                                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                                        <span className="text-sm text-neutral-700">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Risk Mitigation */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-semibold text-neutral-800 mb-4">🛡️ Risk Mitigation</h3>
                            <div className="space-y-3">
                                {selectedChoice.riskMitigation.map((strategy, index) => (
                                    <div key={index} className="flex items-start space-x-3">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                        <span className="text-sm text-neutral-700">{strategy}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Implementation Steps */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-semibold text-neutral-800 mb-4">📋 Implementation Roadmap</h3>
                            <div className="space-y-4">
                                {selectedChoice.implementationSteps.map((step, index) => (
                                    <div key={index} className="flex items-start space-x-4">
                                        <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                                            {index + 1}
                                        </div>
                                        <span className="text-sm text-neutral-700 pt-1">{step.replace(/^\d+\.\s*/, '')}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Retrofit Options */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-semibold text-neutral-800 mb-4">🔧 Included Improvements</h3>
                            <div className="space-y-3">
                                {selectedChoice.retrofitOptions.map((option, index) => (
                                    <div key={index} className="border rounded-lg p-4">
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className="font-medium text-neutral-900">{option.name}</h4>
                                            <span className="text-sm font-medium text-primary-600">
                                                {formatCurrency(option.averageCostPerSqFt)}/sqft
                                            </span>
                                        </div>
                                        <p className="text-sm text-neutral-600 mb-2">{option.description}</p>
                                        <div className="flex justify-between text-xs text-neutral-500">
                                            <span>Energy Savings: {option.averageEnergyReduction}%</span>
                                            <span>GHG Reduction: {option.averageGHGReduction}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Cash Flow Chart */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-neutral-800">📊 Cash Flow Projection</h3>
                                <button
                                    onClick={() => setShowCashFlow(!showCashFlow)}
                                    className="text-primary-600 hover:text-primary-800 text-sm"
                                >
                                    {showCashFlow ? 'Hide' : 'Show'} Details
                                </button>
                            </div>

                            {showCashFlow && (
                                <div className="bg-neutral-50 rounded-lg p-4">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full">
                                            <thead>
                                                <tr className="border-b border-neutral-200">
                                                    <th className="text-left text-xs font-medium text-neutral-600 py-2">Year</th>
                                                    <th className="text-right text-xs font-medium text-neutral-600 py-2">Investment</th>
                                                    <th className="text-right text-xs font-medium text-neutral-600 py-2">Savings</th>
                                                    <th className="text-right text-xs font-medium text-neutral-600 py-2">Loan Payment</th>
                                                    <th className="text-right text-xs font-medium text-neutral-600 py-2">Net Cash Flow</th>
                                                    <th className="text-right text-xs font-medium text-neutral-600 py-2">Cumulative</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedChoice.cashFlow.slice(0, 11).map((flow, index) => (
                                                    <tr key={index} className="border-b border-neutral-100">
                                                        <td className="py-2 text-sm">{flow.year}</td>
                                                        <td className="py-2 text-sm text-right text-red-600">
                                                            {flow.investment > 0 ? `-${EconomicOptimizerService.formatCurrencyShort(flow.investment)}` : '-'}
                                                        </td>
                                                        <td className="py-2 text-sm text-right text-green-600">
                                                            {flow.savings > 0 ? `+${EconomicOptimizerService.formatCurrencyShort(flow.savings)}` : '-'}
                                                        </td>
                                                        <td className="py-2 text-sm text-right text-orange-600">
                                                            {flow.loanPayment > 0 ? `-${EconomicOptimizerService.formatCurrencyShort(flow.loanPayment)}` : '-'}
                                                        </td>
                                                        <td className={`py-2 text-sm text-right font-medium ${
                                                            flow.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                            {flow.netCashFlow >= 0 ? '+' : ''}{EconomicOptimizerService.formatCurrencyShort(flow.netCashFlow)}
                                                        </td>
                                                        <td className={`py-2 text-sm text-right font-medium ${
                                                            flow.cumulative >= 0 ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                            {flow.cumulative >= 0 ? '+' : ''}{EconomicOptimizerService.formatCurrencyShort(flow.cumulative)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Recommendation */}
                        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                            <h4 className="font-medium text-primary-900 mb-2">💡 Economic Recommendation</h4>
                            <p className="text-sm text-primary-800">{selectedChoice.recommendation}</p>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-center space-x-4 mt-8">
                    <button className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors">
                        Generate Financing Application
                    </button>
                    <button className="bg-white text-primary-600 border border-primary-600 px-6 py-3 rounded-lg hover:bg-primary-50 transition-colors">
                        Export Complete Economic Report
                    </button>
                    <button className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors">
                        Contact Implementation Partner
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EconomicOptimizer;
